import React, { useEffect, useRef, useState } from 'react';
import styles from './line.module.css';

// ----- Math helpers -----

function niceMax(val) {
  if (val <= 5) return 5;
  if (val <= 10) return 10;
  const exp = Math.floor(Math.log10(val));
  const mag = Math.pow(10, exp);
  const ratio = val / mag;
  if (ratio <= 2) return 2 * mag;
  if (ratio <= 5) return 5 * mag;
  return 10 * mag;
}

function formatYTick(v) {
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${Math.round(v / 1000)}k`;
  return `${v}`;
}

// Monotone cubic interpolation (Fritsch-Carlson). Smooth curves that never
// overshoot between data points — unlike catmull-rom which can spike on noisy data.
function monotonePath(pts) {
  const n = pts.length;
  if (n === 0) return '';
  if (n === 1) return `M ${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
  if (n === 2) {
    return `M ${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)} L ${pts[1][0].toFixed(
      2
    )},${pts[1][1].toFixed(2)}`;
  }

  // Secant slopes
  const dx = [];
  const dy = [];
  const slope = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1][0] - pts[i][0];
    dy[i] = pts[i + 1][1] - pts[i][1];
    slope[i] = dy[i] / dx[i];
  }

  // Tangents — average of neighbouring secants
  const m = new Array(n);
  m[0] = slope[0];
  m[n - 1] = slope[n - 2];
  for (let i = 1; i < n - 1; i++) m[i] = (slope[i - 1] + slope[i]) / 2;

  // Fritsch-Carlson monotonicity fix
  for (let i = 0; i < n - 1; i++) {
    if (slope[i] === 0) {
      m[i] = m[i + 1] = 0;
      continue;
    }
    const a = m[i] / slope[i];
    const b = m[i + 1] / slope[i];
    const s = a * a + b * b;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      m[i] = t * a * slope[i];
      m[i + 1] = t * b * slope[i];
    }
  }

  // Build cubic bezier path — short handles (0.2×) for steep sides and sharp crests
  let d = `M ${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i];
    const cp1x = pts[i][0] + h * 0.2;
    const cp1y = pts[i][1] + m[i] * h * 0.2;
    const cp2x = pts[i + 1][0] - h * 0.2;
    const cp2y = pts[i + 1][1] - m[i + 1] * h * 0.2;
    d += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${pts[
      i + 1
    ][0].toFixed(2)},${pts[i + 1][1].toFixed(2)}`;
  }
  return d;
}

const PAD = { top: 12, right: 8, bottom: 38, left: 44 };
const Y_TICKS = 4;

/**
 * LineChart — reusable SVG multi-line chart with smooth curves.
 *
 * Props:
 *   data          {Array}   [{ [xKey]: string, ...metricValues }]
 *   lines         {Array}   [{ key, label, color, dashed? }] — which keys to plot
 *   xKey          {string}  field in data used for x-axis labels  (default: 'day')
 *   markers       {Object}  { [xLabel]: ReactNode } — vertical markers; ReactNode is shown as tooltip on hover
 *   markerColor   {string}  fill color for marker dots (default: 'var(--neutral-dark-2)')
 *   className     {string}  extra class on the root div
 */
export const LineChart = ({
  data = [],
  lines = [],
  xKey = 'day',
  markers = {},
  markerColor = 'var(--neutral-dark-2)',
  className = ''
}) => {
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [hover, setHover] = useState(null); // { idx, mouse_x, mouse_y }
  const [active_marker, setActiveMarker] = useState(null); // { label, mouse_x, mouse_y }

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setDims({ w: width, h: height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { w, h } = dims;
  const cw = Math.max(0, w - PAD.right);
  const ch = Math.max(0, h - PAD.top - PAD.bottom);

  const raw_max = Math.max(1, ...data.flatMap((d) => lines.map((l) => d[l.key] || 0)));
  const y_max = niceMax(raw_max);

  const xOf = (i) => {
    if (data.length <= 1) return PAD.left + cw / 2;
    return PAD.left + (i / (data.length - 1)) * cw;
  };
  const yOf = (v) => PAD.top + ch - (v / y_max) * ch;

  const handleMouseMove = (e) => {
    if (!containerRef.current || data.length === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    if (mx < 0 || mx > cw) return;
    const idx = Math.min(data.length - 1, Math.max(0, Math.round((mx / cw) * (data.length - 1))));
    setHover({ idx, mouse_x: e.clientX, mouse_y: e.clientY });
  };

  const flip = (mouse_x, width = 220) => mouse_x + width > window.innerWidth;

  return (
    <div ref={containerRef} className={`${styles.root}${className ? ` ${className}` : ''}`}>
      {w > 0 && h > 0 && (
        <svg
          width={w + PAD.left}
          height={h}
          style={{ display: 'block', overflow: 'visible', marginLeft: -PAD.left }}
          role="img"
          aria-label="Line chart"
        >
          {/* Y grid lines + labels */}
          {Array.from({ length: Y_TICKS + 1 }, (_, i) => {
            const v = Math.round((y_max / Y_TICKS) * i);
            const y = yOf(v);
            return (
              <g key={i}>
                <line
                  x1={PAD.left}
                  x2={PAD.left + cw}
                  y1={y}
                  y2={y}
                  stroke="var(--neutral-dark-6)"
                  strokeWidth={1}
                />
                <text
                  x={PAD.left - 6}
                  y={y + 4}
                  textAnchor="end"
                  fontSize={10}
                  fill="var(--neutral-dark-3)"
                >
                  {formatYTick(v)}
                </text>
              </g>
            );
          })}

          {/* X-axis labels (evenly spaced, max 8) */}
          {(() => {
            const maxLabels = 8;
            const n = data.length;
            if (n === 0) return null;
            const slots = Math.min(n, maxLabels);
            const idxs = Array.from({ length: slots }, (_, j) =>
              Math.round((j * (n - 1)) / (slots - 1))
            );
            const shown = new Set(idxs);
            return data.map((d, i) => {
              if (!shown.has(i)) return null;
              return (
                <text
                  key={i}
                  x={xOf(i)}
                  y={PAD.top + ch + 28}
                  textAnchor="middle"
                  fontSize={10}
                  fill="var(--neutral-dark-3)"
                >
                  {d[xKey]}
                </text>
              );
            });
          })()}

          {/* Marker vertical dashed lines */}
          {data.map((d, i) => {
            if (!markers[d[xKey]]) return null;
            return (
              <line
                key={`mline-${i}`}
                x1={xOf(i)}
                x2={xOf(i)}
                y1={PAD.top}
                y2={PAD.top + ch}
                stroke="var(--neutral-dark-4)"
                strokeDasharray="3 3"
                strokeWidth={1}
              />
            );
          })}

          {/* Metric lines */}
          {lines.map((l) => {
            const pts = data.map((d, i) => [xOf(i), yOf(d[l.key] || 0)]);
            return (
              <path
                key={l.key}
                d={monotonePath(pts)}
                fill="none"
                stroke={l.color}
                strokeWidth={2}
                strokeDasharray={l.dashed ? '6 3' : undefined}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}

          {/* Hover: hairline + dots */}
          {hover !== null && data[hover.idx] && (
            <>
              <line
                x1={xOf(hover.idx)}
                x2={xOf(hover.idx)}
                y1={PAD.top}
                y2={PAD.top + ch}
                stroke="var(--neutral-dark-3)"
                strokeWidth={1}
              />
              {lines.map((l) => (
                <circle
                  key={l.key}
                  cx={xOf(hover.idx)}
                  cy={yOf(data[hover.idx][l.key] || 0)}
                  r={4}
                  fill={l.color}
                  stroke="var(--neutral-light)"
                  strokeWidth={1.5}
                />
              ))}
            </>
          )}

          {/* Marker dots — sit between chart area and x-labels */}
          {data.map((d, i) => {
            const label = d[xKey];
            if (!markers[label]) return null;
            return (
              <circle
                key={`mdot-${i}`}
                cx={xOf(i)}
                cy={PAD.top + ch + 10}
                r={4}
                fill={markerColor}
                stroke="var(--neutral-light)"
                strokeWidth={1.5}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) =>
                  setActiveMarker({ label, mouse_x: e.clientX, mouse_y: e.clientY })
                }
                onMouseLeave={() => setActiveMarker(null)}
              />
            );
          })}

          {/* Invisible overlay — must be last so it captures mouse events on top */}
          <rect
            x={PAD.left}
            y={PAD.top}
            width={cw}
            height={ch}
            fill="transparent"
            aria-hidden="true"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHover(null)}
          />
        </svg>
      )}

      {/* Data hover tooltip */}
      {hover !== null && data[hover.idx] && (
        <div
          role="tooltip"
          aria-live="polite"
          className={styles.dataTooltip}
          style={{
            left: flip(hover.mouse_x) ? hover.mouse_x - 14 : hover.mouse_x + 14,
            top: hover.mouse_y - 16,
            transform: flip(hover.mouse_x) ? 'translateX(-100%)' : 'none'
          }}
        >
          <p className={styles.tooltipLabel}>{data[hover.idx][xKey]}</p>
          {lines.map((l) => (
            <div key={l.key} className={styles.tooltipRow}>
              <span className={styles.tooltipDot} style={{ backgroundColor: l.color }} />
              <span>
                {l.label}: {(data[hover.idx][l.key] || 0).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Marker hover tooltip */}
      {active_marker && markers[active_marker.label] && (
        <div
          role="tooltip"
          className={styles.markerTooltip}
          style={{
            left: flip(active_marker.mouse_x, 240)
              ? active_marker.mouse_x - 14
              : active_marker.mouse_x + 14,
            top: active_marker.mouse_y - 16,
            transform: flip(active_marker.mouse_x, 240) ? 'translateX(-100%)' : 'none'
          }}
        >
          {markers[active_marker.label]}
        </div>
      )}
    </div>
  );
};
